import { SUPPORTED_LOCALES } from '@diario/common';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import authEn from '../i18n/en/auth.json';
import commonEn from '../i18n/en/common.json';
import experiencesEn from '../i18n/en/experiences.json';
import invitationsEn from '../i18n/en/invitations.json';
import joinsEn from '../i18n/en/joins.json';
import legalEn from '../i18n/en/legal.json';
import moderationEn from '../i18n/en/moderation.json';
import puertaEn from '../i18n/en/puerta.json';
import rolesEn from '../i18n/en/roles.json';
import safetyTipsEn from '../i18n/en/safetyTips.json';
import shellEn from '../i18n/en/shell.json';
import authEs from '../i18n/es/auth.json';
import commonEs from '../i18n/es/common.json';
import experiencesEs from '../i18n/es/experiences.json';
import invitationsEs from '../i18n/es/invitations.json';
import joinsEs from '../i18n/es/joins.json';
import legalEs from '../i18n/es/legal.json';
import moderationEs from '../i18n/es/moderation.json';
import puertaEs from '../i18n/es/puerta.json';
import rolesEs from '../i18n/es/roles.json';
import safetyTipsEs from '../i18n/es/safetyTips.json';
import shellEs from '../i18n/es/shell.json';

/**
 * Sincroniza `document.documentElement.lang` con el idioma resuelto por i18next. Sin esto el
 * documento se sigue anunciando en el idioma inicial del HTML servido aunque la interfaz cambie
 * de idioma (defecto de accesibilidad: un lector de pantalla pronuncia el otro idioma con la
 * fonética equivocada). Se usa `resolvedLanguage` — no `language` — porque i18next ya lo resuelve
 * contra `supportedLngs` (p. ej. `es-ES` del navegador se resuelve a `es`); el `split('-')` es una
 * protección adicional por si algún día algún código llega sin resolver. Protegido con
 * `typeof document` porque este módulo puede evaluarse fuera del navegador (SSR, tests).
 */
const sincronizarLangDelDocumento = (): void => {
	if (typeof document === 'undefined') {
		return;
	}
	const idiomaResuelto = i18next.resolvedLanguage ?? i18next.language ?? SUPPORTED_LOCALES[0];
	document.documentElement.lang = idiomaResuelto.split('-')[0] ?? idiomaResuelto;
};

/**
 * Inicialización de i18next (ADR-0008). Un namespace por feature; a `common` se suman
 * `auth` (pantalla de acceso y sesión) y `roles` (senda de roles) en la iteración 2,
 * `experiences` (crear experiencia: asistente, puertas, arranque rápido, éxito y ficha
 * compartida) en la iteración 3, `invitations` (reparto, lista y consejos de contexto del
 * enlace de organizador) + `safetyTips` (los cinco recordatorios de seguridad, documento 04
 * §7.3) en la iteración 4, `joins` (unirse: la ficha pública, las cuatro redirecciones, el
 * cobro del segundo plan del grumete, "¿qué puedes aportar?", el tablón y avisar de que no se
 * va, documentos 05a/05b) en la iteración 5, y `moderation` (el centro de mando: el panel de
 * fases y tareas rápidas, la ficha de solicitudes y el cierre con pase de lista, documentos
 * 06a/06b) en la iteración 6, y `shell` (cabecera, inicio mínimo) + `puerta` (las tres pantallas
 * estáticas: bienvenida, instalar, saber más — copy literal del cliente, documento 2026-09-02)
 * en la tarea T-1.1 de fundación visual (sin consumidor todavía: llegan en la oleada 3). Los
 * recursos se importan de forma estática para que el bundler los incluya y TypeScript pueda
 * derivar los tipos de las claves.
 */
export const inicializarI18n = async (): Promise<void> => {
	await i18next
		.use(LanguageDetector)
		.use(initReactI18next)
		.init({
			defaultNS: 'common',
			detection: { caches: ['localStorage'], order: ['localStorage', 'navigator'] },
			fallbackLng: SUPPORTED_LOCALES[0],
			interpolation: { escapeValue: false },
			resources: {
				en: {
					auth: authEn,
					common: commonEn,
					experiences: experiencesEn,
					invitations: invitationsEn,
					joins: joinsEn,
					legal: legalEn,
					moderation: moderationEn,
					puerta: puertaEn,
					roles: rolesEn,
					safetyTips: safetyTipsEn,
					shell: shellEn,
				},
				es: {
					auth: authEs,
					common: commonEs,
					experiences: experiencesEs,
					invitations: invitationsEs,
					joins: joinsEs,
					legal: legalEs,
					moderation: moderationEs,
					puerta: puertaEs,
					roles: rolesEs,
					safetyTips: safetyTipsEs,
					shell: shellEs,
				},
			},
			supportedLngs: [...SUPPORTED_LOCALES],
		});

	sincronizarLangDelDocumento();
	// El selector de idioma de la cabecera llama a `i18next.changeLanguage`; este listener es lo
	// único que mantiene el atributo `lang` al día en cada cambio (T-1.7).
	i18next.on('languageChanged', sincronizarLangDelDocumento);
};

export { i18next };
